import { useProvider } from "@/hooks/useProvider";
import { Button } from "@/components/ui/button";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";

const ProviderProfile = () => {
  const { profile } = useProvider();
  const navigate = useLocaleNavigate();

  if (!profile) {
    return (
      <div className="p-8 border border-border bg-card">
        <p className="text-caption text-center">Profil wird geladen...</p>
      </div>
    );
  }

  return (
    <div className="p-8 border border-border bg-card space-y-8">
      <div>
        <h2 className="text-headline mb-2">Unternehmensprofil</h2>
        <p className="text-caption">Ihre Stammdaten und Kontaktinformationen</p>
      </div>

      <div className="divider-line" />

      {/* Stammdaten */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <span className="text-micro block mb-1">Firmenname</span>
          <p className="text-body">{profile.company_name}</p>
        </div>
        {profile.legal_name && (
          <div>
            <span className="text-micro block mb-1">Rechtlicher Name</span>
            <p className="text-body">{profile.legal_name}</p>
          </div>
        )}
        {profile.vat_id && (
          <div>
            <span className="text-micro block mb-1">USt-IdNr.</span>
            <p className="text-body">{profile.vat_id}</p>
          </div>
        )}
        {profile.website && (
          <div>
            <span className="text-micro block mb-1">Website</span>
            <p className="text-body">{profile.website}</p>
          </div>
        )}
      </div>

      <div className="divider-line" />

      {/* Adresse */}
      <div>
        <h3 className="text-subheadline mb-4">Adresse</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <span className="text-micro block mb-1">Straße</span>
            <p className="text-body">{profile.address_line1 || "—"}</p>
          </div>
          <div>
            <span className="text-micro block mb-1">PLZ</span>
            <p className="text-body">{profile.postal_code || "—"}</p>
          </div>
          <div>
            <span className="text-micro block mb-1">Stadt</span>
            <p className="text-body">{profile.city || "—"}</p>
          </div>
          <div>
            <span className="text-micro block mb-1">Land</span>
            <p className="text-body">{profile.country || "DE"}</p>
          </div>
        </div>
      </div>

      <div className="divider-line" />

      {/* Kontaktdaten */}
      <div>
        <h3 className="text-subheadline mb-4">Hauptkontakt</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <span className="text-micro block mb-1">Ansprechpartner</span>
            <p className="text-body">{profile.contact_name}</p>
            {profile.contact_role && (
              <p className="text-caption">{profile.contact_role}</p>
            )}
          </div>
          {profile.contact_email && (
            <div>
              <span className="text-micro block mb-1">E-Mail</span>
              <p className="text-body">{profile.contact_email}</p>
            </div>
          )}
          {profile.phone && (
            <div>
              <span className="text-micro block mb-1">Telefon</span>
              <p className="text-body">{profile.phone}</p>
            </div>
          )}
        </div>
      </div>

      {/* Weitere Kontakte */}
      {Array.isArray((profile as any).contacts) && (profile as any).contacts.length > 0 && (
        <>
          <div className="divider-line" />
          <div>
            <h3 className="text-subheadline mb-4">Weitere Ansprechpartner</h3>
            <div className="space-y-4">
              {((profile as any).contacts as any[]).map((c: any, i: number) => (
                <div key={c.id || i} className="border border-border p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-micro block mb-1">Name</span>
                      <p className="text-body">{c.name}</p>
                      {c.role && <p className="text-caption">{c.role}</p>}
                    </div>
                    {c.email && (
                      <div>
                        <span className="text-micro block mb-1">E-Mail</span>
                        <p className="text-body">{c.email}</p>
                      </div>
                    )}
                    {c.phone && (
                      <div>
                        <span className="text-micro block mb-1">Telefon</span>
                        <p className="text-body">{c.phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="divider-line" />

      <div className="grid grid-cols-2 gap-6">
        <div>
          <span className="text-micro block mb-1">Status</span>
          <p className={`text-body ${profile.is_suspended ? "text-destructive" : "text-accent"}`}>
            {profile.is_suspended ? "Suspendiert" : "Aktiv"}
          </p>
        </div>
        <div>
          <span className="text-micro block mb-1">Monatslimit</span>
          <p className="text-body">{profile.monthly_opportunity_limit} Anfragen / Monat</p>
        </div>
        <div>
          <span className="text-micro block mb-1">Genutzt (Monat)</span>
          <p className="text-body">{profile.monthly_opportunities_used} / {profile.monthly_opportunity_limit}</p>
        </div>
      </div>

      <div className="divider-line" />

      <div className="flex gap-4">
        <Button variant="quiet" onClick={() => navigate("/profile/edit")}>
          Profil bearbeiten
        </Button>
      </div>
    </div>
  );
};

export default ProviderProfile;
